import { useEffect, useRef } from 'react';

import {
  createAutosaveHydrationState,
  observeAutosaveLiveSnapshot,
  resetAutosaveBaseline,
  shouldFireDebouncedAutosave,
  type AutosaveHydrationState,
} from '@/core/hooks/autosave-hydration';
import { useDebounce } from '@/core/hooks/use-debounce';

type UseDebouncedAutosaveOptions = {
  /** Serialized live form/state snapshot. */
  snapshot: string;
  /**
   * Server/committed baseline. `null` disables autosave (not ready).
   * Change this when hydrating from the server or after a successful save.
   */
  baseline: string | null;
  enabled?: boolean;
  delayMs: number;
  onSave: () => void;
};

/**
 * Debounced autosave that ignores mount/hydrate noise.
 * Pair with `useAutosaveCue` inside `onSave` for status.
 */
export function useDebouncedAutosave({
  snapshot,
  baseline,
  enabled = true,
  delayMs,
  onSave,
}: UseDebouncedAutosaveOptions): void {
  const debouncedSnapshot = useDebounce(snapshot, delayMs);
  const gateRef = useRef<AutosaveHydrationState>(
    createAutosaveHydrationState(),
  );
  const onSaveRef = useRef(onSave);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    gateRef.current = resetAutosaveBaseline(gateRef.current, baseline);
  }, [baseline]);

  useEffect(() => {
    if (!enabled || baseline == null) return;

    gateRef.current = observeAutosaveLiveSnapshot(gateRef.current, snapshot);

    if (
      !shouldFireDebouncedAutosave(gateRef.current, snapshot, debouncedSnapshot)
    ) {
      return;
    }

    onSaveRef.current();
  }, [snapshot, debouncedSnapshot, baseline, enabled]);
}
